import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { isEmpty, get } from 'lodash';
import { connect } from 'react-redux';
import { DropdownToggle, DropdownMenu, Dropdown, DropdownItem } from 'reactstrap';
import { MDTApp } from '../../../../../actions/bootAction.js';
import { MDTApi } from '../../../../../services/mdtapi.js';
import { IconSet } from '../../../../atoms/mdt-iconset/index.js';
import { sendEventTrackingData } from '../../../../../helpers/commonUtils.js';
import { MDTAnalytics } from '../../../../../services/mdtanalytics.js';
import { userInfoSucessLanguageUpdate, setLanguage } from '../../../../../actions/MeActions.js';
import './language.css';

const LanguageSelector = (props) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [language, setLanguage] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState({
        key: '',
        value: 'English',
    });
    const [culture, setCulture] = useState(
        !!localStorage.getItem('I18N_LANGUAGE_CMS') &&
            localStorage.getItem('I18N_LANGUAGE_CMS') !== 'undefined'
            ? localStorage.getItem('I18N_LANGUAGE_CMS')
            : 'en'
    );

    const { loginStatus, locale } = props;

    useEffect(() => {
        if (props.userInfo) {
            setCulture(props.userInfo.culture);
        }
        getLanguage(locale);
    }, []);

    useEffect(() => {
        if (window.next18) {
            window.next18.changeLanguage(culture);
        }
        localStorage.setItem('I18N_LANGUAGE', culture);
        localStorage.setItem('I18N_LANGUAGE_CMS', culture);
    }, [culture]);

    const getLanguage = (lang) => {
        MDTApi.call('api.common.culture', {
            method: 'GET',
            successCallback: (res) => handleResponse(res, lang),
        });
    };

    const viewTranslationsEnabled = () => {
        if (octopusFeatures?.isViewTranslationEnabled === 'true') {
            return true;
        }

        // NOTE: If octopus is not defined verify that we are in localhost 20230328:Alevale
        if (
            octopusFeatures?.isViewTranslationEnabled === '#{viewTranslatorModeEnabled}' &&
            window.location.hostname.includes('localhost')
        ) {
            return true;
        }
        return false;
    };

    const removeUpperCaseText = () => {
        // NOTE: This will override the text-transform: uppercase; in the css
        // so translators know what should be the right key, we use these selectors
        // because we want to overwrite the custom CSS in any of the elements of the page
        // and the only way to do so is to use a more specific CSS rule that affects them 20230315:Alevale
        const styles = `
      body #root .MDTApp * {
          text-transform: unset !important;
      }
    `;

        const styleSheet = document.createElement('style');
        styleSheet.id = 'customTextTransformStyle';
        styleSheet.innerText = styles;
        const existingStyleSheet = document.getElementById('customTextTransformStyle');
        if (!existingStyleSheet) {
            document.head.appendChild(styleSheet);
        }
    };

    const restoreUpperCaseText = () => {
        const styleSheet = document.getElementById('customTextTransformStyle');
        if (styleSheet) {
            styleSheet.remove();
        }
    };

    const handleResponse = (res, changedLang) => {
        try {
            if (!isEmpty(res)) {
                const lang = !!changedLang
                    ? changedLang
                    : window.next18
                      ? window.next18.language
                      : culture;
                const selectedObj = res.find((lan) => lan.key === lang);
                res = res.sort(alphabeticSort);

                if (viewTranslationsEnabled()) {
                    res.push({ key: 'translatorMode', value: 'Translation Keys' });
                }

                setLanguage(res);

                if (!!selectedObj) {
                    setCulture(selectedObj.key);
                    setSelectedLanguage(selectedObj);
                    props.setLanguage(selectedObj);
                    props.langugaeSelectorCallBack(res, selectedObj);
                } else {
                    props.langugaeSelectorCallBack(res, selectedLanguage);
                }
            }
        } catch (e) {
            console.log('culture error ', e);
        }
    };

    useEffect(() => {
        if (dropdownOpen) {
            const eventParamObj = {
                eventCategory: 'globalHeader',
                eventAction: 'language',
                eventLabel: props.pageName,
                processLabels: false,
            };
            sendEventTrackingData(eventParamObj);
        }
    }, [dropdownOpen, props]);

    const toggle = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const trackLanguageChange = (languageValue) => {
        const eventParamObj = {
            eventCategory: 'globalHeader',
            eventAction: 'languageSelected',
            eventLabel: languageValue,
            processLabels: false,
        };
        sendEventTrackingData(eventParamObj);
    };

    const alphabeticSort = (a, b) => {
        if (a.value < b.value) {
            return -1;
        }
        if (a.value > b.value) {
            return 1;
        }
        return 0;
    };

    const changeValue = (lang) => {
        window.next18.changeLanguage(lang);
        const selectedObj = language.find((lan) => lan.key === lang);
        setSelectedLanguage(selectedObj);

        if (lang === 'translatorMode') {
            removeUpperCaseText();
            return;
        }
        restoreUpperCaseText();

        setCulture(selectedObj.key);

        const languageValue = get(selectedObj, 'value', 'en');
        if (loginStatus) updateUserProfile(lang);
        if (!loginStatus) getLanguage(lang);
        if (window.next18) {
            MDTAnalytics.track({
                siteInfo: {
                    siteLanguage: languageValue,
                },
            });
        }
        language.sort(alphabeticSort);
        trackLanguageChange(languageValue);
    };

    const updateUserProfile = (selectedLang) => {
        const data = {
            Culture: {
                key: selectedLang,
            },
        };
        MDTApi.setDefaultPathVar('userId', props.userId);
        MDTApi.call('api.common.user', {
            method: 'PATCH',
            data,
            successCallback: handleSuccessData,
        });
    };

    const handleSuccessData = (res) => {
        getLanguage();
        props.languages(res.culture);
    };

    return (
        <div
            className={classNames('lang-selector', {
                'logout-enable': !loginStatus,
            })}
        >
            <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                <DropdownToggle caret>
                    <span
                        className={classNames('body2 p-active display-name', {
                            'pr-5': !loginStatus,
                        })}
                    >
                        <IconSet name={props?.iconName || 'Language-new'} />{' '}
                        <span
                            className={classNames('name body2 p-active', {
                                'd-none d-sm-block': loginStatus,
                            })}
                            title={selectedLanguage.value}
                        >
                            {selectedLanguage.value}
                        </span>
                    </span>
                </DropdownToggle>
                {!isEmpty(language) && (
                    <DropdownMenu>
                        <div key='lang-active'>
                            <DropdownItem divider />
                            <DropdownItem
                                className='dropdown-itemSelected'
                                title={selectedLanguage.value}
                                onClick={() => changeValue(selectedLanguage.key)}
                            >
                                <div className='body2 p-active'>{selectedLanguage.value}</div>
                            </DropdownItem>
                            <DropdownItem divider />
                        </div>
                        {language.map((lang, index, arr) => {
                            return lang.key !== selectedLanguage.key ? (
                                <div
                                    key={`lang-${index}`}
                                    data-test={`lang-${index}`}
                                    data-locale={lang.key}
                                >
                                    <DropdownItem
                                        title={lang.value}
                                        onClick={() => changeValue(lang.key)}
                                    >
                                        <div className='body2 p-active'>{lang.value}</div>
                                    </DropdownItem>
                                    {index < arr.length - 1 && <DropdownItem divider />}
                                </div>
                            ) : null;
                        })}
                    </DropdownMenu>
                )}
            </Dropdown>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        userId: get(state, 'meDetails.user.sub', null),
        loginStatus: get(state, 'meDetails.meState.LOGGED_IN', false),
        pageName: state.page,
        userInfo: state.AccountDetails.user || {},
        locale: get(state, 'location.query.locale', ''),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        pageRefresh: () => dispatch(MDTApp.initBootstrap()),
        languages: (lng) => dispatch(userInfoSucessLanguageUpdate(lng)),
        setLanguage: (lng) => dispatch(setLanguage(lng)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LanguageSelector);
